/**
 * shared/howRelayWorks.ts — the "How Relay Works" content module (RELAY_F2).
 *
 * track:track-write-the-build-spec-for-the-how-relay-works-content-module-agent-tool-and-validator-warning
 * (HRW-1, spec #49640). One source for the copy: the page (HRW-2), the
 * get_how_relay_works MCP tool, GET /api/relay/how-relay-works and @relay all
 * render from these exports, so they cannot drift apart.
 *
 * Copy is verbatim from #49602 (stages 1-8, header, loop label) and #49379
 * section 3 (reference panels), plus #49401 for the legend and the Log entry
 * path. Pure module: no imports from server/ or src/.
 */

import type {
  HowRelayWorksStage,
  HowRelayWorksReference,
  HowRelayWorksEntryPath,
} from "./howRelayWorks/types";
import { setUpStage } from "./howRelayWorks/set-up";
import { describeTheProjectStage } from "./howRelayWorks/describe-the-project";
import { specTheWorkStage } from "./howRelayWorks/spec-the-work";
import { writeTheTestsStage } from "./howRelayWorks/write-the-tests";
import { boardItStage } from "./howRelayWorks/board-it";
import { buildAndReviewStage } from "./howRelayWorks/build-and-review";
import { deliverItStage } from "./howRelayWorks/deliver-it";
import { testTheFeatureStage } from "./howRelayWorks/test-the-feature";
import { pagesReference } from "./howRelayWorks/pages";
import { teamAndKeysReference } from "./howRelayWorks/team-and-keys";
import { rulesReference } from "./howRelayWorks/rules";
import { atRelayReference } from "./howRelayWorks/at-relay";
import { howDoIReference } from "./howRelayWorks/how-do-i";
export type {
  HowRelayWorksWho,
  HowRelayWorksStage,
  HowRelayWorksReferenceSection,
  HowRelayWorksReference,
  HowRelayWorksEntryPath,
} from "./howRelayWorks/types";

export const HEADER =
  "From idea to tested feature. You decide at the start and the end; AI does the work in between.";

/**
 * TAF-A (spec #49872, design #49863 §4.0) — the 4-step guide strip at the top
 * of every Test-A-Feature view. Lives here so the page and @relay quote the
 * same words.
 */
export const TEST_A_FEATURE_STEPS: readonly string[] = [
  "Pick a feature",
  "Review what it should do",
  "Approve the tests",
  "Run them",
];

export const LOOP_LABEL = "Fail? It goes back to the backlog.";

export const LEGEND: string[] = [
  "You decide",
  "AI does the work",
  "Either, or both",
  "The Log",
];

export const STAGES: HowRelayWorksStage[] = [
  setUpStage,
  describeTheProjectStage,
  specTheWorkStage,
  writeTheTestsStage,
  boardItStage,
  buildAndReviewStage,
  deliverItStage,
  testTheFeatureStage,
];

export const REFERENCES: HowRelayWorksReference[] = [
  pagesReference,
  teamAndKeysReference,
  rulesReference,
  atRelayReference,
  howDoIReference,
];

export const ENTRY_PATHS: HowRelayWorksEntryPath[] = [
  { name: "Full project setup", goesTo: "set-up" },
  { name: "Just a repo", goesTo: "spec-the-work" },
  {
    name: "Use the Log as your backlog",
    goesTo: "the Log band",
    subLine:
      "Post ideas, bugs and notes, and stop there if that's all you need. When one's ready, it moves up to Spec the work, or straight to Board it if it's small and clear.",
  },
];

/** The 13 deep-link slugs: the 8 stages, then the 5 references. */
export const SLUGS: string[] = [
  ...STAGES.map((s) => s.slug),
  ...REFERENCES.map((r) => r.slug),
];

/** Old slugs that still resolve, mapped to the slug they became. Not panels of their own. */
export const SLUG_ALIASES: Record<string, string> = { "merge-and-deploy": "deliver-it" };

/** The panel slug for a slug or legacy alias; unknown slugs pass through. */
export function resolveSlug(slug: string): string {
  return Object.prototype.hasOwnProperty.call(SLUG_ALIASES, slug) ? SLUG_ALIASES[slug] : slug;
}

/** Origin for "Open in Relay" links; the tool has no request origin to use. */
export const DEFAULT_ORIGIN = "https://easyrelay.app";

export function openInRelayLine(slug: string, origin: string = DEFAULT_ORIGIN): string {
  return `Open in Relay: ${origin}/#${slug}`;
}

function bullets(items: string[]): string {
  return items.map((i) => `- ${i}`).join("\n");
}

export function toOverview(): string {
  const lines: string[] = [`# How Relay Works`, "", HEADER, "", "## The 8 stages"];
  for (const s of STAGES) {
    lines.push(`${s.number}. ${s.name} (${s.whoLabel}) - ${s.oneLiner} [${s.slug}]`);
  }
  lines.push("", "## Reference");
  for (const r of REFERENCES) {
    lines.push(`- ${r.name} - ${r.oneLiner} [${r.slug}]`);
  }
  lines.push("", "## Where to start");
  for (const e of ENTRY_PATHS) {
    lines.push(`- ${e.name} → ${e.goesTo}${e.subLine ? ` (${e.subLine})` : ""}`);
  }
  lines.push("", "Call get_how_relay_works({section: <slug>}) for one panel.");
  return lines.join("\n");
}

function stageMarkdown(s: HowRelayWorksStage): string {
  const out: string[] = [
    `# Stage ${s.number}: ${s.name}`,
    "",
    `Who: ${s.whoLabel}${s.whoSubLine ? ` (${s.whoSubLine})` : ""}`,
    "",
    s.oneLiner,
    "",
    "## What this step is",
    s.whatItIs,
    "",
    "## What you do",
    bullets(s.youDo),
    "",
    "## What your AI partner does",
    bullets(s.aiDoes),
    "",
  ];
  if (s.howItEnds) out.push("## How it ends", bullets(s.howItEnds), "");
  out.push("## If something goes wrong", s.ifWrong, "");
  if (s.note) out.push("## Note", s.note, "");
  out.push("## Go there", s.goThere);
  if (s.secondaryGoThere) out.push(s.secondaryGoThere);
  return out.join("\n");
}

function referenceMarkdown(r: HowRelayWorksReference): string {
  const out: string[] = [`# Reference: ${r.name}`, "", r.oneLiner, ""];
  for (const sec of r.sections) out.push(`## ${sec.title}`, bullets(sec.items), "");
  if (r.goThere) out.push("## Go there", r.goThere);
  return out.join("\n").trimEnd();
}

/** Slug that returns the whole guide in one document. */
export const ALL_SECTION = "all";

/**
 * The whole guide: header, overview, all 8 stage panels, the 4 reference
 * panels, the entry paths, then one line listing every panel's link (no
 * per-panel "Open in Relay" line).
 */
export function toAll(origin: string = DEFAULT_ORIGIN): string {
  const overview: string[] = [`# How Relay Works`, "", HEADER, "", "## The 8 stages"];
  for (const s of STAGES) overview.push(`${s.number}. ${s.name} (${s.whoLabel}) - ${s.oneLiner} [${s.slug}]`);
  overview.push("", "## Reference");
  for (const r of REFERENCES) overview.push(`- ${r.name} - ${r.oneLiner} [${r.slug}]`);
  const entry: string[] = ["# Where to start"];
  for (const e of ENTRY_PATHS) entry.push(`- ${e.name} → ${e.goesTo}${e.subLine ? ` (${e.subLine})` : ""}`);
  const links = `Open in Relay: ${SLUGS.map((slug) => `${slug} ${origin}/#${slug}`).join(" · ")}`;
  return [
    overview.join("\n"),
    ...STAGES.map(stageMarkdown),
    ...REFERENCES.map(referenceMarkdown),
    entry.join("\n"),
    links,
  ].join("\n\n");
}

/** No argument returns the overview; "all" returns the whole guide; a valid slug returns that panel; an unknown slug throws. */
export function toMarkdown(rawSection?: string): string {
  if (rawSection === undefined || rawSection === "") return toOverview();
  const section = resolveSlug(rawSection);
  if (section === ALL_SECTION) return toAll();
  const stage = STAGES.find((s) => s.slug === section);
  if (stage) return stageMarkdown(stage);
  const ref = REFERENCES.find((r) => r.slug === section);
  if (ref) return referenceMarkdown(ref);
  throw new Error(`Unknown section "${section}". Valid sections: ${SLUGS.join(", ")}, ${ALL_SECTION}`);
}

export type HowRelayWorksResult =
  | { ok: true; section: string | null; markdown: string }
  | { ok: false; error: string; slugs: string[] };

/**
 * The tool and the REST route both render through this, so their output is
 * identical. A valid slug gets the "Open in Relay" line appended; the
 * overview does not.
 */
export function renderHowRelayWorks(section?: string, origin: string = DEFAULT_ORIGIN): HowRelayWorksResult {
  const slug = section === undefined || section === "" ? null : resolveSlug(section);
  try {
    if (slug === ALL_SECTION) return { ok: true, section: slug, markdown: toAll(origin) };
    const markdown = toMarkdown(slug ?? undefined);
    return {
      ok: true,
      section: slug,
      markdown: slug ? `${markdown}\n\n${openInRelayLine(slug, origin)}` : markdown,
    };
  } catch (err: unknown) {
    return { ok: false, error: err instanceof Error ? err.message : String(err), slugs: [...SLUGS] };
  }
}
